import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { RenderForm } from './render-form';
import { PrintIcon } from './print-icon';
import { FormActionsMenu } from '../other/FormActionsMenu';
import { usePermission } from '../../container/permissionContext';

function PrintModal({ isOpen, closeModal, formData, deleteForm, archiveForm, activateForm, hideActions, onPrint }) {
  const { hasPermissionByCodeName, isSuperAdmin, isCompanySystemAdministrator } = usePermission();
  const { deleted_at, id: formId } = formData ?? {};
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);
  const [initData, setInitData] = useState({});

  const handleDeleteForm = async () => {
    try {
      if (deleteForm) {
        await deleteForm(formId);
        closeModal();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleActivateForm = async () => {
    try {
      if (activateForm) {
        await activateForm(formId);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleArchiveForm = useCallback(async () => {
    try {
      if (archiveForm) {
        await archiveForm(formData);
        closeModal();
      }
    } catch (e) {
      console.log(e);
    }
  }, [formId]);

  const formActionsMenu = useMemo(() => {
    let menu = [];

    if ((isSuperAdmin() || isCompanySystemAdministrator()) && hasPermissionByCodeName('corporate_library_management')) {
      menu = [
        ...(deleted_at
          ? [
              <button style={{ color: '#12b76a' }} className="btn btn-full" onClick={handleActivateForm}>
                <svg className="mr-2" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_1484_3438)">
                    <path
                      d="M18.3332 9.2333V9.99997C18.3321 11.797 17.7503 13.5455 16.6743 14.9848C15.5983 16.4241 14.0859 17.477 12.3626 17.9866C10.6394 18.4961 8.79755 18.4349 7.1119 17.8121C5.42624 17.1894 3.98705 16.0384 3.00897 14.5309C2.03089 13.0233 1.56633 11.24 1.68457 9.4469C1.80281 7.65377 2.49751 5.94691 3.66507 4.58086C4.83263 3.21482 6.41049 2.26279 8.16333 1.86676C9.91617 1.47073 11.7501 1.65192 13.3915 2.3833M18.3332 3.3333L9.99984 11.675L7.49984 9.17497"
                      stroke="#12B76A"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1484_3438">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Activate
              </button>,
            ]
          : [
              <button className="btn btn-full" onClick={handleArchiveForm}>
                <i className="icon-icon-x mr-2" />
                Archive
              </button>,
            ]),
        <button className="btn btn-full btn-remove" onClick={handleDeleteForm}>
          <i className="icon-remove mr-2" />
          Delete
        </button>,
      ];
    }

    return menu;
  }, [
    deleted_at,
    handleActivateForm,
    handleArchiveForm,
    handleDeleteForm,
    hasPermissionByCodeName,
    isCompanySystemAdministrator,
    isSuperAdmin,
  ]);

  const handelCloseModal = () => closeModal();

  useEffect(() => {
    if (formData) {
      if (formData?.submitted_data?.length) {
        const form_fields = formData?.form_fields.map((form_field) => {
          const submitted_item = formData?.submitted_data?.find((item) => form_field.id === item.id);
          const saved_item = formData?.saved_data?.find((item) => form_field.id === item.id);

          if (submitted_item) {
            return isReadOnly(
              {
                ...submitted_item,
                options: {
                  ...submitted_item.options,
                  top: form_field.options.top,
                  height: form_field.options.height,
                  width: form_field.options.width,
                  left: form_field.options.left,
                  innerElemHeight: form_field.options.innerElemHeight,
                },
              },
              formData,
            );
          }

          if (saved_item) {
            return {
              ...saved_item,
              options: {
                ...saved_item.options,
                top: form_field.options.top,
                height: form_field.options.height,
                width: form_field.options.width,
                left: form_field.options.left,
                innerElemHeight: form_field.options.innerElemHeight,
              },
            };
          }

          return isReadOnly(form_field, formData);
        });

        setInitData({ ...initData, form_fields: form_fields });
      } else if (formData?.form_fields) {
        if (formData?.saved_data?.length) {
          const form_fields = formData?.form_fields.map((form_field) => {
            const saved_data = formData?.saved_data?.find((item) => form_field.id === item.id);

            if (saved_data) {
              return saved_data;
            }

            return isReadOnly(form_field, formData);
          });

          setInitData({ form_fields: JSON.parse(JSON.stringify(form_fields)), ...initData });
        } else {
          const form_fields = formData?.form_fields.map((item) => {
            return isReadOnly(item, formData);
          });

          setInitData({ form_fields: form_fields, ...initData });
        }
      }
    }

    // eslint-disable-next-line
  }, [formData]);

  const isReadOnly = (element, res) => {
    if (element.type === 'name' || element.type === 'address' || element.type === 'checkbox') {
      element.inputs.map((input) => {
        input['readOnly'] = res.can_edit_previous_submissions ? '' : 'readonly';

        return input;
      });
    }

    if (element.type === 'table') {
      element.rows.map((row) => {
        if (row.cols) {
          row.cols.forEach((col) => {
            col['readOnly'] = res.can_edit_previous_submissions ? null : 'readonly';
          });
        }
        return row;
      });
    }

    element.options['readOnly'] = res.can_edit_previous_submissions ? null : 'readonly';

    return element;
  };

  const handelInitSettings = useCallback(
    (res, key) => {
      try {
        if (initData[key] && key === 'form_fields' && Array.isArray(res)) {
          setInitData((prevState) => {
            return {
              ...prevState,
              [key]: [...res],
            };
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    [initData],
  );

  const handleOnPrint = async () => {
    setIsPDFGenerating(true);
    await onPrint();
    setIsPDFGenerating(false);
  };

  return (
    <Modal
      className="modal-default print-modal general-form-tab-content"
      isOpen={isOpen}
      onRequestClose={handelCloseModal}
      contentLabel="Example Modal"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        <div style={{ display: 'inline-flex', gap: 16 }}>
          <button
            onClick={onPrint ? handleOnPrint : window.print}
            className={'btn btn-outline  btn-outline-secondary d-flex'}
            disabled={isPDFGenerating}
          >
            <PrintIcon className={'mr-2'} />
            Print
          </button>
          {!hideActions && <FormActionsMenu menu={formActionsMenu} />}
        </div>
        <button className="btn btn-icon" onClick={handelCloseModal}>
          <i className="icon-icon-x"></i>
        </button>
      </h2>

      <div className={'print'}>
        {formData && (
          <RenderForm
            isPrint
            isSubmitter
            handelInitSettings={handelInitSettings}
            title={
              formData.title_options
                ? {
                    ...formData?.title_options,
                    options: { ...formData?.title_options.options, value: formData.name },
                  }
                : formData.name
            }
            formElement={initData?.form_fields}
          />
        )}
      </div>
    </Modal>
  );
}

export default memo(PrintModal);
